import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, FileText, FileSignature, Download, Upload, Trash2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_META = {
  receipt: { Icon: Receipt, ar: 'إيصال المعاملة', en: 'Receipt', color: '#0284c7' },
  document: { Icon: FileText, ar: 'المستند / الموافقة', en: 'Document', color: '#7c3aed' },
  signed_document: { Icon: FileSignature, ar: 'المستند بعد التوقيع', en: 'Signed Document', color: '#059669' },
};

const DocSlot = ({ type, info, canUpload, canDelete, onUpload, onDownload, onDelete, isRTL, busy, waitingLabel }) => {
  const inputRef = useRef(null);
  const { Icon, ar, en, color } = DOC_META[type];
  const label = isRTL ? ar : en;
  const has = !!info;
  const title = has ? `${label} — ${info.name || ''} (${info.uploaded_at ? new Date(info.uploaded_at).toLocaleDateString('en-GB') : ''})` : (waitingLabel || label);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} title={title} data-testid={`doc-slot-${type}`}>
      <button type="button" disabled={busy}
        onClick={() => has ? onDownload(type, info) : (canUpload && inputRef.current?.click())}
        data-testid={has ? `doc-download-${type}` : `doc-upload-${type}`}
        style={{ background: has ? `${color}18` : 'transparent', border: `1px ${has ? 'solid' : 'dashed'} ${has ? color : '#cbd5e1'}`, borderRadius: 6, padding: '2px 5px', cursor: (has || canUpload) ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 3, color: has ? color : '#94a3b8', fontSize: 10, fontWeight: 700 }}>
        {busy ? <Loader2 style={{ width: 11, height: 11 }} className="animate-spin" /> : <Icon style={{ width: 11, height: 11 }} />}
        {has ? <Download style={{ width: 9, height: 9 }} /> : (canUpload ? <Upload style={{ width: 9, height: 9 }} /> : null)}
      </button>
      {has && canUpload && (
        <button type="button" onClick={() => inputRef.current?.click()} title={isRTL ? 'استبدال' : 'Replace'} data-testid={`doc-replace-${type}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, color: '#64748b' }}><Upload style={{ width: 10, height: 10 }} /></button>
      )}
      {has && canDelete && (
        <button type="button" onClick={() => onDelete(type)} title={isRTL ? 'حذف' : 'Delete'} data-testid={`doc-delete-${type}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, color: '#dc2626' }}><Trash2 style={{ width: 10, height: 10 }} /></button>
      )}
      <input ref={inputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} data-testid={`doc-file-input-${type}`}
        onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(type, f); e.target.value = ''; }} />
    </span>
  );
};

export const TxDocuments = ({ txId, kind, documents = {}, isAdmin, headers, isRTL, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const hasReceipt = !!documents.receipt;
  const hasDoc = !!documents.document;
  const hasSigned = !!documents.signed_document;

  const upload = async (type, file) => {
    setBusy(type);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/transactions/${kind}/${txId}/documents/${type}`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success(isRTL ? 'تم رفع الملف' : 'File uploaded');
      onChanged?.(txId, res.data.documents);
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'فشل الرفع' : 'Upload failed')); }
    setBusy(null);
  };
  const download = async (type, info) => {
    setBusy(type);
    try {
      const res = await axios.get(`${API}/transactions/${kind}/${txId}/documents/${type}`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error(isRTL ? 'فشل التحميل' : 'Download failed'); }
    setBusy(null);
  };
  const remove = async (type) => {
    if (!window.confirm(isRTL ? 'حذف هذا الملف؟' : 'Delete this file?')) return;
    setBusy(type);
    try {
      const res = await axios.delete(`${API}/transactions/${kind}/${txId}/documents/${type}`, { headers });
      onChanged?.(txId, res.data.documents);
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'فشل الحذف' : 'Delete failed')); }
    setBusy(null);
  };

  const common = { onUpload: upload, onDownload: download, onDelete: remove, isRTL };
  const showReceipt = isAdmin || hasReceipt;
  const showDoc = isAdmin || hasDoc;
  const showSigned = isAdmin || hasDoc;
  if (!showReceipt && !showDoc) return <span style={{ color: '#cbd5e1' }}>-</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }} data-testid={`tx-documents-${txId}`}>
      {showReceipt && <DocSlot type="receipt" info={documents.receipt} canUpload={isAdmin} canDelete={isAdmin} busy={busy === 'receipt'} {...common} />}
      {showDoc && <DocSlot type="document" info={documents.document} canUpload={isAdmin} canDelete={isAdmin} busy={busy === 'document'} {...common} />}
      {showSigned && (
        <DocSlot type="signed_document" info={documents.signed_document} canUpload={hasDoc} canDelete={hasSigned} busy={busy === 'signed_document'}
          waitingLabel={hasDoc ? (isRTL ? '⏳ بانتظار رفع العميل للمستند الموقّع' : '⏳ Waiting for client signed upload') : (isRTL ? 'ارفع المستند أولاً' : 'Upload document first')} {...common} />
      )}
      {showSigned && hasDoc && (
        <span style={{ fontSize: 9, fontWeight: 700, color: hasSigned ? '#059669' : '#d97706' }} data-testid={`signed-status-${txId}`}>
          {hasSigned ? (isRTL ? '✓ تم الرفع' : '✓ Uploaded') : (isRTL ? '⏳ بانتظار' : '⏳ Waiting')}
        </span>
      )}
    </span>
  );
};

export default TxDocuments;
